import { PollyClient, SynthesizeSpeechCommand } from "@aws-sdk/client-polly";
import {SSMLSplit} from "ssml-split";

const splitter = new SSMLSplit({
  synthesizer: "aws",
  breakParagraphsAboveHardLimit: true,
  extraSplitChars: ".;?!",
})

export default class TtsClient {
  /**
   * @param {{
   *  credentialProviderUrl?: string,
   *  credentials?: object,
   *  outputBucket?: string
   * }} options
   */
  constructor(options) {
    /** @type{string} */
    this.credentialProviderUrl = options.credentialProviderUrl || "/rest/v1/tts_credentials"
    this.credentials = options.credentials
    this.outputBucket = options.outputBucket
    this.usesCredentialProvider = !options.credentials
  }

  /**
   * @returns {Promise<void>}
   */
  async fetchCredentials() {
    let response = await fetch(this.credentialProviderUrl, { method: "POST" });
    if (!response.ok) {
      throw new Error(`Unable to load text-to-speech credentials (${response.status})`);
    }

    let credentials = await response.json();
    if (!credentials.access_key_id || !credentials.secret_access_key) {
      throw new Error("The text-to-speech credential response was incomplete");
    }

    this.credentials = {
      accessKeyId: credentials.access_key_id,
      secretAccessKey: credentials.secret_access_key,
      sessionToken: credentials.session_token,
      region: "us-east-1"
    }

    if (!this.outputBucket) {
      this.outputBucket = credentials.output_bucket
    }
  }

  /**
   * @param {string} text
   * @param {string} speed
   * @returns {Promise<{url: string, marks: object[]}[]>}
   */
  async synthesizeSpeech(text, speed) {
    if (!this.credentials) {
      await this.fetchCredentials();
    }

    try {
      return await this.performSynthesis(text, speed);
    } catch (error) {
      // Temporary AWS credentials can expire while the item player remains open.
      // Refresh them once, while leaving malformed SSML and other 400s retryable
      // from the player UI instead of repeatedly sending the same bad request.
      if (this.usesCredentialProvider && isAuthenticationError(error)) {
        this.credentials = null;
        await this.fetchCredentials();
        return this.performSynthesis(text, speed);
      }

      throw error;
    }
  }

  /**
   * @param {string} text
   * @param {string} speed
   * @returns {Promise<{url: string, marks: object[]}[]>}
   */
  async performSynthesis(text, speed) {
    let batches = splitter.split(text);
    if (batches.length == 0) {
      throw new Error("No text was available for speech synthesis");
    }

    let client = new PollyClient({
      credentials: this.credentials,
      region: "us-east-1"
    });

    let promises = batches.map((batch) => {
      let wrappedText = batch.replace("<speak>", `<speak><prosody rate="${speed}">`).replace("</speak>", "</prosody></speak>");
      let params = {
        Text: wrappedText,
        OutputFormat: "mp3",
        TextType: "ssml",
        VoiceId: "Joanna",
        SampleRate: "22050",
      }
      let audioProm = withNetworkRetry(() => {
        return client.send(new SynthesizeSpeechCommand(params));
      }).then((response) => {
        return readAudioStream(response.AudioStream);
      });

      let speechMarksProm = client.send(new SynthesizeSpeechCommand({...params, OutputFormat: "json", SpeechMarkTypes: ["word", "ssml"]})).then((response) => {
        return readSpeechMarks(response.AudioStream);
      });

      // Highlight timing is supplemental. If that request fails but Polly
      // returned audio, continue playback without highlighting.
      return Promise.allSettled([audioProm, speechMarksProm]).then((results) => {
        if (results[0].status == "rejected") {
          throw results[0].reason;
        }

        if (results[0].value.reduce((size, chunk) => size + chunk.byteLength, 0) == 0) {
          throw new Error("Polly returned empty audio");
        }

        return [
          results[0].value,
          results[1].status == "fulfilled" ? results[1].value : []
        ];
      });
    });

    try {
      let chunks = await Promise.all(promises);

      return chunks.map((chunk) => {
        let blob = new Blob(chunk[0], { type: "audio/mpeg" });
        let url = URL.createObjectURL(blob);
        return {url, marks: chunk[1]}
      });
    } finally {
      client.destroy();
    }
  }
}

/**
 * @param {ReadableStream | Blob | undefined} stream
 * @returns {Promise<Uint8Array[]>}
 */
export async function readAudioStream(stream) {
  if (!stream) {
    throw new Error("Polly returned no audio stream");
  }

  if (stream instanceof Blob) {
    return [new Uint8Array(await stream.arrayBuffer())];
  }

  if (!(stream instanceof ReadableStream)) {
    throw new Error("Polly returned an unsupported audio stream");
  }

  let chunks = [];
  let reader = stream.getReader();
  try {
    while (true) {
      let { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }

  return chunks;
}

/**
 * Decode newline-delimited speech marks without assuming that stream chunks
 * end on JSON record boundaries.
 *
 * @param {ReadableStream | Blob | undefined} stream
 * @returns {Promise<object[]>}
 */
export async function readSpeechMarks(stream) {
  let chunks = await readAudioStream(stream);
  let decoder = new TextDecoder();
  let buffered = "";
  let marks = [];

  for (let chunk of chunks) {
    buffered += decoder.decode(chunk, { stream: true });
    let lines = buffered.split("\n");
    buffered = lines.pop() || "";
    lines.forEach((line) => {
      if (line.trim()) marks.push(JSON.parse(line));
    });
  }

  buffered += decoder.decode();
  if (buffered.trim()) marks.push(JSON.parse(buffered));
  return marks;
}

/**
 * The AWS SDK version used by the item player does not retry browser fetch
 * failures. Retry only the required audio request; speech marks remain
 * best-effort so a highlight failure cannot delay playback.
 *
 * @template T
 * @param {() => Promise<T>} request
 * @param {(delayMs: number) => Promise<void>} [wait]
 * @returns {Promise<T>}
 */
export async function withNetworkRetry(request, wait = delay) {
  const retryDelays = [250, 750];

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      if (!isNetworkError(error) || attempt >= retryDelays.length) {
        throw error;
      }
      await wait(retryDelays[attempt]);
    }
  }
}

/**
 * @param {number} delayMs
 */
function delay(delayMs) {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

/**
 * @param {unknown} error
 */
function isNetworkError(error) {
  if (!error || typeof error != "object") return false;
  let networkError = /** @type {{name?: string, message?: string}} */ (error);
  return networkError.name == "NetworkError" ||
    (networkError.name == "TypeError" && /fetch|network|load failed|socket/i.test(networkError.message || ""));
}

/**
 * @param {unknown} error
 */
function isAuthenticationError(error) {
  if (!error || typeof error != "object") return false;
  let errorName = /** @type {{name?: string}} */ (error).name;
  return [
    "ExpiredToken",
    "ExpiredTokenException",
    "InvalidClientTokenId",
    "InvalidSignatureException",
    "UnrecognizedClientException"
  ].includes(errorName || "");
}
